import {
  HIDDEN,
  PARTNER_POPULATION_RULE,
  PARTNER_RULE,
  POPULATION_RULE,
} from '@/constants/data_shares';
import { useDataSharesStore, usePartnersStore } from '@/stores';
import type { OutgoingSharingRule } from '@/types/data_shares';
import type { Partner } from '@/types/partners';

/**
 * Shared source of truth for whether a partner is on a population's default
 * sharing settings, used by both the Sharing Settings Hub and the Sharing Dashboard.
 */
export default function usePopulationDefaultUsage() {
  const dataSharesStore = useDataSharesStore();
  const partnersStore = usePartnersStore();

  function getDefaultSharingRule(
    populationId: number,
  ): OutgoingSharingRule | undefined {
    return dataSharesStore.getOutgoingSharingRules({
      ruleType: POPULATION_RULE,
      populationId,
    })[0];
  }

  function getPartnerOverrides(populationId: number): {
    overridesByPartner: Record<number, OutgoingSharingRule>;
    nullPartnerOverride: OutgoingSharingRule | undefined;
  } {
    const overridesByPartner: Record<number, OutgoingSharingRule> = {};
    let nullPartnerOverride: OutgoingSharingRule | undefined;
    const partnerRules = dataSharesStore.getOutgoingSharingRules({
      ruleType: PARTNER_RULE,
      populationId,
    });

    partnerRules.forEach((rule) => {
      if (rule.partner_org_id) {
        overridesByPartner[rule.partner_org_id] = rule;
      } else {
        nullPartnerOverride = rule;
      }
    });

    return { overridesByPartner, nullPartnerOverride };
  }

  function getPopulationOverrideCounts(populationId: number): {
    popToPopCountByPartner: Map<number, number>;
    nullPartnerRuleCount: number;
  } {
    const popToPopRules = dataSharesStore.getOutgoingSharingRules({
      ruleType: PARTNER_POPULATION_RULE,
      populationId,
    });

    const popToPopCountByPartner = new Map<number, number>();
    let nullPartnerRuleCount = 0;
    for (const rule of popToPopRules) {
      if (rule.partner_org_id) {
        popToPopCountByPartner.set(
          rule.partner_org_id,
          (popToPopCountByPartner.get(rule.partner_org_id) ?? 0) + 1,
        );
      } else {
        nullPartnerRuleCount++;
      }
    }

    return { popToPopCountByPartner, nullPartnerRuleCount };
  }

  /**
   * Splits partners by whether their effective sharing rule matches the
   * population default. A partner-level rule that matches the default on both
   * visibility and field preset still counts as using the default.
   */
  function getPartnersUsingDefault(populationId: number): {
    partnersUsingDefaults: Partner[];
    partnersNotUsingDefaults: Partner[];
  } {
    const defaultRule = getDefaultSharingRule(populationId);
    const defaultVisibility = defaultRule?.visibility ?? HIDDEN;
    const defaultPresetId = defaultRule?.data_share_presets_id ?? null;

    const { overridesByPartner, nullPartnerOverride } =
      getPartnerOverrides(populationId);
    const { popToPopCountByPartner, nullPartnerRuleCount } =
      getPopulationOverrideCounts(populationId);

    const partnersUsingDefaults: Partner[] = [];
    const partnersNotUsingDefaults: Partner[] = [];

    const partners = partnersStore.partnerOrgs.filter(
      (partner) => !partner.offline_partner && !partner.virtual_partner,
    );

    for (const partner of partners) {
      const effectiveRule =
        overridesByPartner[partner.id] ?? nullPartnerOverride ?? defaultRule;

      const matchesDefaults =
        (effectiveRule?.visibility ?? HIDDEN) === defaultVisibility &&
        (effectiveRule?.data_share_presets_id ?? null) === defaultPresetId;

      const overridesCount =
        (popToPopCountByPartner.get(partner.id) ?? 0) + nullPartnerRuleCount;

      if (matchesDefaults && !overridesCount) {
        partnersUsingDefaults.push(partner);
      } else {
        partnersNotUsingDefaults.push(partner);
      }
    }

    return { partnersUsingDefaults, partnersNotUsingDefaults };
  }

  return {
    getDefaultSharingRule,
    getPartnerOverrides,
    getPartnersUsingDefault,
  };
}
